import { goSolidApi } from "@/composables/useGoSolidApi";
import {
  useAsyncTask,
  type AsyncTaskStartResponse,
} from "@/composables/useAsyncTask";
import { useLocalStorage } from "@vueuse/core";
import { computed, toValue, watch, type MaybeRefOrGetter } from "vue";

// Celery keeps task results for 24h (result_expires in config.py); past that the status endpoint
// can no longer answer for the id, so there is nothing to re-attach to.
const RESULT_RETENTION_MS = 24 * 60 * 60 * 1000;

export interface GridOperatorReportProgress {
  current: number;
  total: number;
  current_company_id: string;
}

interface StoredReportRun {
  taskId: string;
  startedAt: string;
  completedAt?: string;
}

async function startReportGeneration(): Promise<AsyncTaskStartResponse> {
  const response = await goSolidApi.post(
    "/api/registry/hknr/grid-operator-issuance-data-report"
  );
  return response.data;
}

/**
 * The TSO declaration report runs for one registry round trip per company, so it is remembered in
 * localStorage per account and re-attached on setup: leaving the page does not lose the run or the
 * finished file.
 */
export function useBulkIssuanceReport(accountId: MaybeRefOrGetter<number>) {
  const task = useAsyncTask<unknown, GridOperatorReportProgress>();
  const storedRun = useLocalStorage<StoredReportRun | null>(
    `hknr-tso-report:${toValue(accountId)}`,
    null,
    { serializer: { read: JSON.parse, write: JSON.stringify } }
  );

  const completedAt = computed(() => storedRun.value?.completedAt ?? null);

  const triggerReport = async () => {
    await task.execute(startReportGeneration);
    if (task.taskId.value && task.state.value === "processing") {
      storedRun.value = {
        taskId: task.taskId.value,
        startedAt: new Date().toISOString(),
      };
    }
  };

  const reset = () => {
    storedRun.value = null;
    task.reset();
  };

  watch(task.state, (state) => {
    if (
      state === "success" &&
      storedRun.value &&
      !storedRun.value.completedAt
    ) {
      storedRun.value = {
        ...storedRun.value,
        completedAt: new Date().toISOString(),
      };
    } else if (state === "failed") {
      storedRun.value = null;
    }
  });

  if (storedRun.value) {
    const retainedFrom =
      storedRun.value.completedAt ?? storedRun.value.startedAt;
    if (isOlderThan(retainedFrom, RESULT_RETENTION_MS)) {
      storedRun.value = null;
    } else {
      task.attach(storedRun.value.taskId);
    }
  }

  return {
    state: task.state,
    progress: task.progress,
    completedAt,
    triggerReport,
    downloadReport: task.downloadResult,
    cancelReport: task.cancel,
    reset,
  };
}

function isOlderThan(isoDate: string | null, ageMs: number): boolean {
  if (!isoDate) return false;
  return Date.now() - new Date(isoDate).getTime() > ageMs;
}
